import { useEffect, useState } from 'react';

export interface Department {
  id: string;
  name: string;
  children: Department[];
  jobs: {
    id: number;
    title: string;
  }[];
}

type JSONResponse = {
  departments?: Array<Department>;
  status?: number;
  error?: string;
};

const fetchDepartments = async (
  controller: AbortController,
): Promise<Department[]> => {
  const response = await fetch(
    'https://boards-api.greenhouse.io/v1/boards/zapier/departments?render_as=tree',
    {
      method: 'GET',
      signal: controller.signal,
    },
  );

  const data: JSONResponse = await response.json();

  if (!response.ok) {
    throw new Error(
      'Failed to fetch departments: ' +
        `[${data.status || 'unknown'}] ${data.error || ''}`,
    );
  }

  return data.departments || [];
};

export const useDepartments = () => {
  const [departments, setDepartments] = useState<Department[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const doFetch = async (controller: AbortController) => {
    try {
      const data = await fetchDepartments(controller);
      setDepartments(data);
    } catch (error) {
      console.error(error);
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    doFetch(controller);
    return () => {
      controller.abort();
    };
  }, []);

  return { departments, hasError, isLoading };
};
